// Pulse Pharmacy — pure, dependency-free pharmacy logic.
//
// FEFO batch selection, expiry classification, low-stock, VAT-aware pricing
// and the controlled-register running-balance maths. No I/O — every function
// here is unit-testable and drives both the UI and the ledger postings.

import type {
  BatchAllocation,
  ControlledRegisterEntry,
  DrugSchedule,
  PharmacyBatch,
  PharmacyDrug,
  VatTreatment,
} from '@nako/types';
import { BW_VAT_RATE, stripVat } from './vat';

const round2 = (n: number): number => Math.round(n * 100) / 100;
const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Round a Pula amount to the nearest 5 thebe (cash-friendly shelf pricing). */
export function roundTo005(amount: number): number {
  return Math.round(amount * 20) / 20;
}

// ───── FEFO batch selection ────────────────────────────────────────

/** A batch as far as FEFO cares — a narrow shape so callers can pass either a
 *  full PharmacyBatch row or a trimmed projection. */
export type FefoBatch = Pick<
  PharmacyBatch,
  'id' | 'batch_no' | 'expiry_date' | 'qty_on_hand' | 'unit_cost' | 'received_at'
>;

export interface FefoResult {
  allocations: BatchAllocation[];
  /** Requested qty that could not be covered by available stock. */
  shortfall: number;
}

/**
 * Allocate `qty` across batches First-Expiry-First-Out.
 *
 * Batches are consumed in ascending expiry order (ties broken by earliest
 * received_at) so the shortest-dated stock leaves the shelf first. Batches
 * with no stock are skipped, and — unless `includeExpired` is set — batches
 * that have already expired on `today` are never auto-dispensed.
 */
export function pickBatchesFEFO(
  batches: FefoBatch[],
  qty: number,
  opts: { today?: string; includeExpired?: boolean } = {},
): FefoResult {
  const today = opts.today ?? isoToday();
  const usable = batches
    .filter((b) => b.qty_on_hand > 0)
    .filter((b) => (opts.includeExpired ? true : b.expiry_date >= today))
    .sort((a, b) => {
      if (a.expiry_date !== b.expiry_date) return a.expiry_date < b.expiry_date ? -1 : 1;
      return (a.received_at ?? '') < (b.received_at ?? '') ? -1 : 1;
    });

  const allocations: BatchAllocation[] = [];
  let remaining = round3(qty);
  for (const b of usable) {
    if (remaining <= 0) break;
    const take = round3(Math.min(remaining, b.qty_on_hand));
    if (take <= 0) continue;
    allocations.push({
      batch_id: b.id,
      batch_no: b.batch_no,
      expiry_date: b.expiry_date,
      qty: take,
      unit_cost: b.unit_cost,
    });
    remaining = round3(remaining - take);
  }

  return { allocations, shortfall: remaining > 0 ? remaining : 0 };
}

/** Blended (weighted-average) unit cost across FEFO allocations — the COGS
 *  snapshot stored on a dispense line for margin reporting. */
export function unitCostOf(allocations: BatchAllocation[]): number {
  const totalQty = allocations.reduce((s, a) => s + a.qty, 0);
  if (totalQty <= 0) return 0;
  const totalCost = allocations.reduce((s, a) => s + a.qty * a.unit_cost, 0);
  return round2(totalCost / totalQty);
}

// ───── Expiry ──────────────────────────────────────────────────────

export type ExpiryBand = 'expired' | 'expiring_90' | 'expiring_180' | 'ok';

export interface ExpiryStatus {
  band: ExpiryBand;
  daysToExpiry: number;
}

/** Classify a batch expiry relative to `today` (defaults to today). Drives the
 *  Batches page pill colours: expired → danger, ≤90d → warn, ≤180d → notice. */
export function expiryStatus(expiryDate: string, today: string = isoToday()): ExpiryStatus {
  const days = daysBetween(today, expiryDate);
  let band: ExpiryBand;
  if (days < 0) band = 'expired';
  else if (days <= 90) band = 'expiring_90';
  else if (days <= 180) band = 'expiring_180';
  else band = 'ok';
  return { band, daysToExpiry: days };
}

// ───── Pricing (VAT-aware) ─────────────────────────────────────────

/**
 * Derive a VAT-inclusive shelf price from cost + markup. Standard-rated drugs
 * add VAT on top of the marked-up net; zero/exempt drugs carry no VAT. Result
 * rounded to the nearest 5 thebe.
 */
export function priceFromMarkup(
  unitCost: number,
  markupPct: number,
  vatTreatment: VatTreatment,
  vatRate: number = BW_VAT_RATE,
): number {
  const net = unitCost * (1 + markupPct / 100);
  const gross = vatTreatment === 'standard' ? net * (1 + vatRate) : net;
  return roundTo005(gross);
}

export interface LineTotals {
  /** VAT-inclusive line total. */
  line_total: number;
  /** Net of VAT. */
  net: number;
  vat_amount: number;
}

/**
 * Split a dispensed line into net + VAT. Standard-rated lines have VAT stripped
 * out of the inclusive price; zero/exempt lines contribute no VAT.
 */
export function lineTotals(
  qty: number,
  unitPrice: number,
  vatTreatment: VatTreatment,
  vatRate: number = BW_VAT_RATE,
): LineTotals {
  const gross = round2(qty * unitPrice);
  if (vatTreatment === 'standard') {
    const { net, vat } = stripVat(gross, vatRate);
    return { line_total: gross, net, vat_amount: vat };
  }
  return { line_total: gross, net: gross, vat_amount: 0 };
}

export interface DispenseTotals {
  subtotal: number;
  vat_amount: number;
  total: number;
}

/** Sum line splits into a dispense header. subtotal + vat === total to 2dp. */
export function dispenseTotals(lines: LineTotals[]): DispenseTotals {
  const subtotal = round2(lines.reduce((s, l) => s + l.net, 0));
  const vat_amount = round2(lines.reduce((s, l) => s + l.vat_amount, 0));
  const total = round2(subtotal + vat_amount);
  return { subtotal, vat_amount, total };
}

// ───── Stock levels ────────────────────────────────────────────────

/** On-hand quantity for a drug: sum of unexpired batch stock (defaults to
 *  today; pass includeExpired to count expired stock still physically held). */
export function stockOnHand(
  batches: Array<Pick<PharmacyBatch, 'expiry_date' | 'qty_on_hand'>>,
  opts: { today?: string; includeExpired?: boolean } = {},
): number {
  const today = opts.today ?? isoToday();
  return round3(
    batches
      .filter((b) => (opts.includeExpired ? true : b.expiry_date >= today))
      .reduce((s, b) => s + b.qty_on_hand, 0),
  );
}

/** True when unexpired stock is at or below the drug's reorder level. */
export function isLowStock(
  drug: Pick<PharmacyDrug, 'reorder_level'>,
  batches: Array<Pick<PharmacyBatch, 'expiry_date' | 'qty_on_hand'>>,
  today: string = isoToday(),
): boolean {
  return stockOnHand(batches, { today }) <= drug.reorder_level;
}

export interface LowStockRow {
  drug_id: string;
  on_hand: number;
  reorder_level: number;
}

/** Low-stock list for the reorder report. */
export function lowStockList(
  drugs: Array<Pick<PharmacyDrug, 'id' | 'reorder_level'>>,
  batchesByDrug: Map<string, Array<Pick<PharmacyBatch, 'expiry_date' | 'qty_on_hand'>>>,
  today: string = isoToday(),
): LowStockRow[] {
  const out: LowStockRow[] = [];
  for (const d of drugs) {
    const batches = batchesByDrug.get(d.id) ?? [];
    const on_hand = stockOnHand(batches, { today });
    if (on_hand <= d.reorder_level) {
      out.push({ drug_id: d.id, on_hand, reorder_level: d.reorder_level });
    }
  }
  return out;
}

// ───── Controlled-substances register ──────────────────────────────

/** Next running balance for a controlled-register row. */
export function nextRegisterBalance(prevBalance: number, qtyIn: number, qtyOut: number): number {
  return round3(prevBalance + qtyIn - qtyOut);
}

export interface RegisterCheck {
  /** Entries with the balance each SHOULD carry, in chronological order. */
  expected: Array<Pick<ControlledRegisterEntry, 'id'> & { balance_after: number }>;
  /** Indexes (into the passed array) whose stored balance_after is wrong. */
  discrepancies: number[];
}

/**
 * Recompute running balances for one drug's register (chronological input) and
 * flag rows whose stored balance_after disagrees — the "verify" banner on the
 * Controlled Register page. Detects tampering / missed postings.
 */
export function recomputeRegisterBalances(
  entries: Array<Pick<ControlledRegisterEntry, 'id' | 'qty_in' | 'qty_out' | 'balance_after'>>,
): RegisterCheck {
  const expected: RegisterCheck['expected'] = [];
  const discrepancies: number[] = [];
  let running = 0;
  entries.forEach((e, i) => {
    running = nextRegisterBalance(running, e.qty_in, e.qty_out);
    expected.push({ id: e.id, balance_after: running });
    if (round3(e.balance_after) !== running) discrepancies.push(i);
  });
  return { expected, discrepancies };
}

// ───── Dispense numbering ──────────────────────────────────────────

/** DSP-000123 from a per-business sequence (same shape as invoice numbering). */
export function nextDispenseNo(seq: number): string {
  return `DSP-${String(seq).padStart(6, '0')}`;
}

/** Human label for a schedule, e.g. "S3 (controlled)". */
export function scheduleLabel(schedule: DrugSchedule): string {
  return schedule === 'OTC' ? 'OTC' : `${schedule} (controlled)`;
}

// ───── date helpers (UTC-safe, no timezone drift for date-only maths) ─────

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (both YYYY-MM-DD). Negative if `to` is past. */
function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}
